import { eventNames, useCoreEvents } from '@carvana/core-events';
import Button from '@carvana/ds-button';
import Sparkles from '@carvana/ds-icons/SparklesCvna';
import Skeleton from '@carvana/ds-skeleton';
import Clickable from '@carvana/merch-components/Clickable';
import { datadogRum } from '@datadog/browser-rum';
import cx from 'classnames';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import TrackWrapper from '~/components/common/TrackWrapper';
import { appAssetsCdnUrl } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import formatEvent from '~/utilities/formatEvent';
import optimizeImage from '~/utilities/optimizeImage';
import QuestionButton from './QuestionButton';
import styles from './ShaqbotSection.module.css';
import {
  ShaqbotAvatar,
  ShaqbotHeading,
  ShaqbotSectionContainer
} from './ShaqbotSection.styles';

export default function ShaqbotSection() {
  const trackEvent = useTrackEvent();
  const router = useRouter();
  const [isChatReady, setIsChatReady] = useState(false);
  const [hasProcessedOpenChatParam, setHasProcessedOpenChatParam] =
    useState(false);
  const suggestedQuestions = [
    { id: 'intro-001', text: 'What is Shaqvana?', value: 'What is Shaqvana?' },
    {
      id: 'intro-002',
      text: 'What does Shaqbot do?',
      value: 'What does Shaqbot do?'
    },
    {
      id: 'intro-003',
      text: "What's your favorite car?",
      value: "What's your favorite car?"
    },
    {
      id: 'intro-004',
      text: "What's your favorite memory?",
      value: "What's your favorite memory?"
    }
  ];

  useCoreEvents(eventNames.chatUi.ready, () => {
    setIsChatReady(true);

    trackEvent('Shaqbot Section', {
      action: 'loaded',
      description: 'Chat UI Ready'
    });
    datadogRum.addAction('Chat UI Ready');
  });

  useEffect(() => {
    if (hasProcessedOpenChatParam || !isChatReady) return;

    const { openChat } = router.query;

    if (openChat === 'true') {
      setHasProcessedOpenChatParam(true);
      window.ChatShaqbot?.openChat?.();
      trackEvent('Shaqbot Section', {
        action: 'auto-opened',
        description: 'Chat automatically opened from URL parameter'
      });
    }
  }, [router.query, isChatReady, hasProcessedOpenChatParam, trackEvent]);

  useCoreEvents(eventNames.chatUi.error, () => {
    trackEvent('Shaqbot Section', {
      action: 'loaded',
      description: 'Chat UI Error'
    });
    datadogRum.addAction('Chat UI Error');
  });

  const handleQuestionClick = (question: {
    id: string;
    text: string;
    value: string;
  }) => {
    window.ChatShaqbot?.sendMessage?.(question.value, { qaId: question.id });
  };

  const handleAskSomethingElse = () => {
    window.ChatShaqbot?.openChat?.();
  };

  const imageUrl = optimizeImage(
    `${appAssetsCdnUrl}/images/shaqvana/shaqbot-avatar-audio-bar.png`
  );

  return (
    <TrackWrapper
      event={formatEvent('Shaqbot Section', {
        action: 'viewed'
      })}
    >
      <ShaqbotSectionContainer>
        <Clickable
          className={cx('flex flex-col items-center gap-lg', {
            'cursor-pointer': isChatReady
          })}
          data-analytics-attributes={JSON.stringify({
            text: 'Shaqbot has entered the chat'
          })}
          data-analytics-id="Shaqbot Section"
          data-testid="open-chat"
          onClick={handleAskSomethingElse}
        >
          <ShaqbotAvatar src={imageUrl} alt="Shaqbot Avatar" />
          <ShaqbotHeading data-testid="shaqbot-header">
            Shaqbot has entered the chat
          </ShaqbotHeading>
        </Clickable>
        <p
          className="text-center text-white t-body-md"
          data-testid="shaqbot-description"
        >
          Shaqbot is an A.I. sidekick with the personality of a 7-foot legend
          and the brain fueled by Carvana’s car know-how. Built to chat, joke,
          and kick it with you. Got a question? Shaq’s A.I. twin has the answer.
        </p>

        <div className="flex max-w-[445px] flex-col items-center justify-center gap-xl">
          <div className="flex flex-col items-center justify-center gap-md sm:flex-row sm:flex-wrap">
            {suggestedQuestions.map((question, index) => (
              <QuestionButton
                key={index}
                data-testid="shaqbot-question-button"
                onClick={() => handleQuestionClick(question)}
                text={question.text}
                isLoading={!isChatReady}
              />
            ))}
          </div>

          {isChatReady ? (
            <Button
              className={styles.askSomethingElseButton}
              data-analytics-id="Shaqbot Section"
              data-analytics-attributes={JSON.stringify({
                text: 'Ask something else'
              })}
              onClick={handleAskSomethingElse}
              size="sm"
              variant="primary"
              utility
              data-testid="ask-something-else"
            >
              <Sparkles size={14} />
              Ask something else
            </Button>
          ) : (
            <Skeleton className="rounded-sm" width="184px" height="40px" />
          )}
        </div>
      </ShaqbotSectionContainer>
    </TrackWrapper>
  );
}
